
import React, { useEffect } from 'react';
import { Plus, Columns, ShieldCheck, Database, BarChart2, Loader2 } from 'lucide-react';

interface ChatSidebarProps {
  colorMode: 'light' | 'dark';
  onNewChat: () => void;
  columnMappingData?: any;
  validationData?: any;
  loadData?: any;
  onColumnMappingClick?: () => void;
  onValidationClick?: () => void;
  onLoadClick?: () => void;
  onDashboardClick?: () => void;
  /** Index-aligned to [columnMapping, validation, load] — true = stage is done and should show tick. */
  completedStages?: boolean[];
  isColumnMappingLoading?: boolean;
  isValidationLoading?: boolean;
  isLoadLoading?: boolean;
}

const ChatSidebar: React.FC<ChatSidebarProps> = ({
  colorMode,
  onNewChat,
  columnMappingData,
  validationData,
  loadData,
  onColumnMappingClick,
  onValidationClick,
  onLoadClick,
  onDashboardClick,
  completedStages = [],
  isColumnMappingLoading = false,
  isValidationLoading = false,
  isLoadLoading = false,
}) => {
  const isDark = colorMode === 'dark';

  useEffect(() => {
    if (typeof document !== 'undefined' && !document.getElementById('sidebar-anim-css')) {
      const el = document.createElement('style');
      el.id = 'sidebar-anim-css';
      el.textContent = `
        @keyframes sb-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes sb-pulse { 0%,100%{opacity:1} 50%{opacity:0.55} }
        @keyframes sb-shimmer {
          0%   { background-position: -200% center; }
          100% { background-position:  200% center; }
        }
        .sb-loading-btn {
          background: linear-gradient(90deg, #0f2056 0%, #1e3a8a 40%, #2d58c8 60%, #0f2056 100%) !important;
          background-size: 200% auto !important;
          animation: sb-shimmer 1.8s linear infinite !important;
        }
      `;
      document.head.appendChild(el);
    }
  }, []);

  const stages: {
    key: string;
    label: string;
    data: any;
    onClick?: () => void;
    icon: React.ReactNode;
    isLoading: boolean;
  }[] = [
    { key: 'columnMapping', label: 'Column Mapping', data: columnMappingData, onClick: onColumnMappingClick, icon: <Columns size={15} />, isLoading: isColumnMappingLoading },
    { key: 'validation',    label: 'Validation',     data: validationData,    onClick: onValidationClick,    icon: <ShieldCheck size={15} />, isLoading: isValidationLoading },
    { key: 'load',          label: 'Load to NDB',    data: loadData,          onClick: onLoadClick,          icon: <Database size={15} />,   isLoading: isLoadLoading },
  ];

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', width: '272px', height: '100%', flexShrink: 0,
      background: isDark ? '#0d1117' : '#ffffff',
      borderRight: '1px solid ' + (isDark ? '#1f2937' : '#e8ecf5'),
    }}>
      {/* New Chat Button */}
      <div style={{ padding: '14px 14px 12px', borderBottom: '1px solid ' + (isDark ? '#1c2535' : '#f0f2f8') }}>
        <button
          onClick={onNewChat}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            width: '100%', padding: '10px 16px',
            border: 'none',
            borderRadius: '11px',
            background: '#196ECF',
            cursor: 'pointer',
            fontSize: '13px', fontWeight: 600,
            color: 'white',
            boxShadow: '0 2px 8px rgba(25,110,207,0.28)',
            transition: 'transform 0.15s, box-shadow 0.15s, filter 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 5px 16px rgba(25,110,207,0.42)'; e.currentTarget.style.filter = 'brightness(1.08)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(25,110,207,0.28)'; e.currentTarget.style.filter = 'brightness(1)'; }}
        >
          <Plus size={15} />
          <span>New Chat</span>
        </button>
      </div>

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Workflow Actions */}
      <div style={{
        borderTop: '1px solid ' + (isDark ? '#1c2535' : '#f0f2f8'),
        padding: '12px 12px 6px',
        display: 'flex', flexDirection: 'column', gap: '6px',
      }}>
        <div style={{
          fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.1em', color: isDark ? '#3a4a65' : '#b0b8d0',
          padding: '0 4px 4px',
        }}>
          Workflow
        </div>
        {stages.map((s, stageIndex) => {
          const isDone = !!s.data || s.isLoading;
          const isLoading = s.isLoading;
          const isCompleted = completedStages[stageIndex] ?? false;

          return (
            <button
              key={s.key}
              onClick={isDone && !isLoading ? s.onClick : undefined}
              disabled={!isDone}
              className={isLoading ? 'sb-loading-btn' : ''}
              style={{
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px', width: '100%',
                padding: '10px 13px',
                border: isLoading
                  ? '1.5px solid rgba(96,165,250,0.35)'
                  : isDone
                  ? '1.5px solid rgba(15,32,86,0.0)'
                  : '1.5px solid ' + (isDark ? '#1e2a3a' : '#e8ecf5'),
                borderRadius: '11px',
                background: isLoading
                  ? undefined /* handled by .sb-loading-btn class */
                  : isDone
                  ? 'linear-gradient(135deg, #0f2056 0%, #1a3a90 100%)'
                  : isDark ? '#111827' : '#f4f6fc',
                color: isDone ? '#fff' : isDark ? '#2d3d55' : '#c0c8dc',
                cursor: isDone && !isLoading ? 'pointer' : isLoading ? 'wait' : 'default',
                fontSize: '13px', fontWeight: 600,
                opacity: !isDone ? 0.5 : 1,
                transition: 'transform 0.15s, box-shadow 0.15s, filter 0.15s',
                boxShadow: isDone && !isLoading ? '0 2px 8px rgba(15,32,86,0.22)' : 'none',
                letterSpacing: '0.015em',
              }}
              onMouseEnter={e => {
                if (isDone && !isLoading) {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 18px rgba(15,32,86,0.4)';
                  e.currentTarget.style.filter = 'brightness(1.1)';
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = isDone && !isLoading ? '0 2px 8px rgba(15,32,86,0.22)' : 'none';
                e.currentTarget.style.filter = 'brightness(1)';
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {isLoading
                  ? <Loader2 size={14} style={{ animation: 'sb-spin 0.85s linear infinite', flexShrink: 0 }} />
                  : <span style={{ opacity: isDone ? 1 : 0.5 }}>{s.icon}</span>
                }
                <span style={{ animation: isLoading ? 'sb-pulse 1.2s ease-in-out infinite' : 'none' }}>
                  {isLoading ? `${s.label}…` : s.label}
                </span>
              </span>
              {isCompleted && !isLoading && (
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                  background: 'rgba(134,239,172,0.15)', border: '1.5px solid #86efac',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#86efac" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Dashboard Button */}
      <div style={{ padding: '8px 12px 10px', borderTop: '1px solid ' + (isDark ? '#1c2535' : '#f0f2f8') }}>
        <button
          onClick={onDashboardClick}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            width: '100%', padding: '10px 16px',
            border: 'none',
            borderRadius: '11px',
            background: '#196ECF',
            cursor: 'pointer',
            fontSize: '13px', fontWeight: 600, color: 'white',
            boxShadow: '0 2px 8px rgba(25,110,207,0.28)',
            transition: 'transform 0.15s, box-shadow 0.15s, filter 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 5px 16px rgba(25,110,207,0.42)'; e.currentTarget.style.filter = 'brightness(1.08)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(25,110,207,0.28)'; e.currentTarget.style.filter = 'brightness(1)'; }}
        >
          <BarChart2 size={15} />
          <span>Dashboard</span>
        </button>
      </div>

      {/* Footer */}
      <div style={{
        padding: '7px 16px 10px', textAlign: 'center', fontSize: '10px',
        color: isDark ? '#2a3a52' : '#d0d8ea',
        borderTop: '1px solid ' + (isDark ? '#1c2535' : '#f0f2f8'),
        letterSpacing: '0.03em',
      }}>
        NOVA · Address Maintainance Intelligence
      </div>
    </div>
  );
};

export default ChatSidebar;
